<template>
  <main class="home-view">
    <Header />

    <div class="home-content">
      <v-sheet class="welcome-card" color="surface" elevation="0">
        <p class="welcome-card__eyebrow">Your personalized legal news feed</p>
        <h1>Welcome to LWYR</h1>

        <p v-if="sourceNames.length" class="welcome-card__copy">
          You are currently receiving the latest legal news and insights from
          <template v-for="(name, index) in sourceNames" :key="name">
            <RouterLink
              v-if="sourceMap[name]?.slug"
              :to="`/source/${sourceMap[name].slug}`"
              class="welcome-card__link"
            >
              {{ name }}
            </RouterLink>
            <span v-else>{{ name }}</span
            ><span v-if="index < sourceNames.length - 1">, </span> </template
          >.
        </p>

        <p class="welcome-card__copy">
          To customize your feed, visit your
          <RouterLink
            :to="{ path: '/settings', query: { section: 'topics' } }"
            class="welcome-card__link"
          >
            preferences </RouterLink
          >.
        </p>
      </v-sheet>

      <section class="home-section" aria-labelledby="daily-brief-title">
        <div class="section-header">
          <h2 id="daily-brief-title">Daily Brief</h2>
          <v-btn
            class="section-header__action"
            density="compact"
            to="/daily"
            variant="text"
          >
            See all
            <v-icon end>mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <div v-if="articleState.isLoading" class="card-skeletons">
          <v-skeleton-loader
            class="card-skeleton card-skeleton--featured"
            type="image, article"
          />
          <v-skeleton-loader
            v-for="i in 3"
            :key="i"
            class="card-skeleton"
            type="image, article"
          />
        </div>

        <v-sheet
          v-else-if="!articles.length"
          class="home-empty"
          color="surface"
          elevation="0"
        >
          <v-icon color="accent" size="32"
            >mdi-newspaper-variant-outline</v-icon
          >
          <p>No articles found. Try expanding your filters.</p>
        </v-sheet>

        <template v-else>
          <ArticleCard :article="articles[0]" featured />

          <v-slide-group
            v-if="articles.length > 1"
            class="home-carousel"
            show-arrows
          >
            <v-slide-group-item
              v-for="article in articles.slice(1, 15)"
              :key="article.id"
            >
              <div class="home-carousel__item">
                <ArticleCard :article="article" compact />
              </div>
            </v-slide-group-item>
          </v-slide-group>
        </template>
      </section>

      <Picks />
      <Trending />
    </div>
  </main>
</template>

<script setup>
import { computed } from "vue";

import ArticleCard from "@/components/ArticleCard.vue";
import Picks from "@/components/homeView/Picks.vue";
import Trending from "@/components/homeView/Trending.vue";
import Header from "@/components/general/Header.vue";
import { articleState } from "@/store/articlesStore";
import { preferencesState } from "@/store/preferencesStore";
import { sourceMap } from "@/store/sourceStore";

const articles = computed(() =>
  [...articleState.articles]
    .map((article) => ({
      ...article,
      title: article.title ?? "",
      description: article.description ?? "",
      type: article.type ?? "",
      author: article.author ?? "",
      source: article.source ?? "",
      views: article.views ?? 0,
    }))
    .sort(
      (a, b) =>
        new Date(b.published).getTime() - new Date(a.published).getTime()
    )
);

const sourceNames = computed(() => {
  const selectedSources = Array.isArray(preferencesState.sources)
    ? preferencesState.sources
    : [];

  return selectedSources
    .map((source) => {
      const value =
        typeof source === "string"
          ? source
          : source?.value || source?.id || source?.name;
      const entry = Object.entries(sourceMap).find(
        ([name, details]) => name === value || details.id === value
      );
      return entry?.[0] || value;
    })
    .filter(Boolean);
});
</script>

<style scoped>
.home-view {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
}

.home-content {
  width: min(1100px, 100%);
  margin: 0 auto;
  padding: 20px 0 80px;
}

.welcome-card {
  margin-bottom: 56px;
  padding: clamp(28px, 5vw, 52px);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 8px;
  background-image: linear-gradient(
    135deg,
    rgba(var(--v-theme-accent), 0.08),
    transparent 52%
  );
  text-align: center;
}

.welcome-card__eyebrow {
  margin: 0 0 10px;
  color: rgb(var(--v-theme-accent));
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.welcome-card h1 {
  margin: 0 0 18px;
  color: rgb(var(--v-theme-on-surface));
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 850;
  line-height: 1.15;
}

.welcome-card__copy {
  max-width: 760px;
  margin: 10px auto 0;
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 1.65;
}

.welcome-card__link {
  color: rgb(var(--v-theme-accent));
  font-weight: 750;
  text-decoration: none;
}

.welcome-card__link:hover,
.welcome-card__link:focus-visible {
  text-decoration: underline;
}

.home-section {
  margin-bottom: 64px;
}

.section-header {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-header h2 {
  margin: 0;
  color: rgb(var(--v-theme-on-surface));
  font-size: clamp(1.55rem, 3vw, 2rem);
  font-weight: 850;
  line-height: 1.2;
  text-transform: uppercase;
}

.section-header__action {
  flex: 0 0 auto;
  border-radius: 8px;
  font-weight: 750;
}

.home-carousel {
  width: 100%;
  margin-top: 16px;
}

.home-carousel__item {
  width: 318px;
  height: 100%;
  padding: 4px 8px 12px;
}

.card-skeletons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.card-skeleton {
  min-height: 420px;
  border-radius: 8px;
}

.card-skeleton--featured {
  grid-column: 1 / -1;
}

.home-empty {
  display: grid;
  gap: 12px;
  place-items: center;
  min-height: 220px;
  padding: 32px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 8px;
  color: rgba(var(--v-theme-on-surface), 0.68);
  text-align: center;
}

.home-empty p {
  margin: 0;
}

:deep(.home-carousel .v-slide-group__prev),
:deep(.home-carousel .v-slide-group__next) {
  min-width: 44px;
  color: rgb(var(--v-theme-accent));
}

@media (max-width: 700px) {
  .home-view {
    width: min(100% - 24px, 1180px);
  }

  .home-content {
    padding-top: 12px;
  }

  .welcome-card {
    margin-bottom: 42px;
    padding: 28px 20px;
  }

  .home-section {
    margin-bottom: 48px;
  }

  .home-carousel__item {
    width: min(82vw, 310px);
    padding-left: 4px;
    padding-right: 8px;
  }

  .card-skeletons {
    grid-template-columns: 1fr;
  }

  :deep(.home-carousel .v-slide-group__prev),
  :deep(.home-carousel .v-slide-group__next) {
    display: none;
  }
}
</style>
